import { editManualCharge } from 'graphql/ledger/mutations';
import { getLedgerListings } from 'graphql/ledger/queries';
import { useState } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import { listingItemtype } from 'types/ledger';
import useAlert from 'utils/alert';
import { validateAllowedKindTypes } from 'utils/filterItemsbykind';
import { capitalize } from 'utils/helpers';
import { AccountingType } from 'v2-types/order';

import { getPayoffErrorMessage } from '../../services/errors';
import { addManualCharge } from './services/mutations';
import { EntryLedgerType } from './services/types';

const useAddRecipts = (
  handleClose: () => void,
  prevData: AccountingType | null,
  kindAllowedEntry?: string | string[],
  codeAllowedEntry?: string | string[],
  resolver?: Resolver<EntryLedgerType, object> | undefined,
  mortgageId?: string
) => {
  const { t } = useTranslation();
  const [createErrors, setcreateErrors] = useState<string[]>([]);
  const { id: orderId } = useParams<{ id: string }>();
  const showAlert = useAlert();
  const [allowedCodes, setAllowedCodes] = useState<listingItemtype[]>([]);
  const [allowedKinds, setAllowedKinds] = useState<listingItemtype[]>([]);
  const methods = useForm<EntryLedgerType>({
    defaultValues: {
      entryDate: prevData?.entry_date || null,
      description: prevData?.description || '',
      code: prevData?.code || '',
      amount: prevData?.amount || 0,
      kind: prevData?.kind || '',
      letter: prevData?.letter,
      number: prevData?.number,
      amount_override: prevData?.amount_override || prevData?.amount,
      amount_calculated: prevData?.amount_calculated || 0,
      months: prevData?.months || 0,
    },
    resolver,
  });

  const { mutate: AddManualcharge, ...responseAddManualcharge } = useMutation(addManualCharge, {
    onSuccess: (data) => {
      if (data) {
        showAlert(capitalize(t('dialogs:order-updated')), 'success');
        responseAddManualcharge.reset();
        methods.reset();
        handleClose();
      }
    },
    onError: (e: any) => {
      const responseErrors = e?.response?.errors;
      const errorsMapped: string[] = Array.isArray(responseErrors) && responseErrors.length > 0
        ? responseErrors.map((error: any) => error?.message || getPayoffErrorMessage(error, t))
        : [getPayoffErrorMessage(e, t)];
      setcreateErrors(errorsMapped);
    },
  });

  const { mutate: EditManualcharge, ...responseEditManualcharge } = useMutation(editManualCharge, {
    onSuccess: (data) => {
      if (data) {
        showAlert(capitalize(t('dialogs:order-updated')), 'success');
        responseEditManualcharge.reset();
        methods.reset();
        handleClose();
      }
    },
  });
  const getLedgerAllowedKinds = useQuery(
    ['allowed-ledger-kinds', 'allowed_ledger_kinds'],
    () => getLedgerListings('allowed_ledger_kinds'),
    {
      enabled: prevData === null,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data.getListing.entries) {
          const kinds = validateAllowedKindTypes(data?.getListing?.entries, kindAllowedEntry);
          setAllowedKinds(kinds);
          if (!prevData) {
            methods.setValue('kind', kinds[0].code);
          }
        }
      },
    }
  );

  const getLedgerAllowedCodes = useQuery(
    ['allowed-ledger-Codes', 'allowed_ledger_codes'],
    () => getLedgerListings('allowed_ledger_codes'),
    {
      enabled: prevData === null,
      refetchOnWindowFocus: false,
      onSuccess: (data) => {
        if (data.getListing.entries) {
          const codes = validateAllowedKindTypes(data?.getListing?.entries, codeAllowedEntry);
          setAllowedCodes(codes);
          if (!prevData) {
            methods.setValue('code', codes[0].code);
          }
        }
      },
    }
  );

  const handleEntry = methods.handleSubmit((data) => {
    if (prevData) {
      EditManualcharge({
        id: orderId!,
        accountingId: prevData?._id!,
        data: {
          amount_override: parseFloat(data.amount_override as unknown as string) || 0,
          entry_date: data?.entryDate || null,
          letter: data.letter === '' ? null : data.letter,
          number: data.number ? parseFloat(data.number as unknown as string) : null,
          // eslint-disable-next-line radix
          months: data.months ? parseInt(data.months as unknown as string) : null,
        },
      });
      return;
    }
    AddManualcharge({
      id: orderId!,
      mortgageId,
      ...data,
      number: data.number ? parseFloat(data.number as unknown as string) : null,
      amount: parseFloat(data.amount as unknown as string) || 0,
      // eslint-disable-next-line radix
      months: data.months ? parseInt(data.months as unknown as string) : null,
      entryDate: data?.entryDate ? data?.entryDate : null,
      letter: data.letter === '' ? null : data.letter,
    });
  });

  return {
    allowedCodes,
    allowedKinds,
    loadingKinds: getLedgerAllowedKinds.isLoading,
    loadingCodes: getLedgerAllowedCodes.isLoading,
    loadingEditEntry: responseEditManualcharge.isLoading,
    loadingAddNewEntry: responseAddManualcharge.isLoading,
    handleEntry,
    methods,
    createErrors,
    t,
  };
};

export default useAddRecipts;
